import React from "react";
import {
  Modal,
  View,
  StyleSheet,
  TouchableOpacity,
  StatusBar,
  Image,
} from "react-native";
import { AppText } from "@/components/AppText";
import { LinearGradient } from "expo-linear-gradient";
import { AntDesign } from "@expo/vector-icons";
import LottieConfetti from "@/components/LottieConfetti";
import ShareUrlComponent from "@/components/ShareUrlComponent";
import { Offer } from "@/types/offer";
import useUserStore from "@/store/user";

type Props = {
  visible: boolean;
  onClose: () => void;
  offer: Offer;
};

const OfferSuccessModal: React.FC<Props> = ({ visible, onClose, offer }) => {
  const companyName = offer.name;
  const { userProfile } = useUserStore();
  const shareUrl = `https://usesparrow.onelink.me/h5cL/iamwg19q?deep_link_value=${userProfile?.id}`;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <StatusBar barStyle="light-content" backgroundColor="transparent" />
      <LinearGradient
        colors={["#26913B", "#243D3C"]}
        style={styles.gradient}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
      >
        <View style={styles.contentContainer}>
          {/* Close button */}
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <AntDesign name="close" size={24} color="#FFFFFF" />
          </TouchableOpacity>

          {/* Confetti animation */}
          <LottieConfetti active={visible} />

          {/* Main content container */}
          <View style={styles.mainContainer}>
            {/* Company icon */}
            <View style={styles.companyIconContainer}>
              <View style={styles.companyIcon}>
                <View style={styles.whiteBackground} />
                {offer.image ? (
                  <Image
                    source={{ uri: offer.image }}
                    style={styles.companyIconImage}
                    resizeMode="contain"
                  />
                ) : (
                  <AppText style={styles.companyIconText}>
                    {companyName.charAt(0).toUpperCase()}
                  </AppText>
                )}
              </View>
            </View>

            {/* Success message */}
            <AppText style={styles.title}>
              Your signup offer is{"\n"}on its way!
            </AppText>

            <AppText style={styles.subtitle}>
              Once your signup is confirmed, we&apos;ll send you a
              notification. Payments are sent out 2-3 weeks after confirmation.
            </AppText>

            {/* Share URL */}
            <ShareUrlComponent shareUrl={shareUrl} />

            {/* Invitation text */}
            <AppText style={styles.inviteText}>
              Invite a friend and you&apos;ll both{"\n"}get an extra $5 refund.
            </AppText>
          </View>
        </View>
      </LinearGradient>
    </Modal>
  );
};

const styles = StyleSheet.create({
  gradient: {
    flex: 1,
    paddingHorizontal: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  contentContainer: {
    width: "100%",
    maxWidth: 480,
  },
  closeButton: {
    position: "absolute",
    top: 60,
    right: 16,
    zIndex: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  mainContainer: {
    alignItems: "center",
  },
  companyIconContainer: {
    marginBottom: 25,
  },
  companyIcon: {
    width: 104,
    height: 104,
    borderRadius: 16,
    backgroundColor: "#F3F3F3",
    borderWidth: 1,
    borderColor: "#EBEBEB",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    position: "relative",
  },
  whiteBackground: {
    position: "absolute",
    width: 104,
    height: 104,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
  },
  companyIconImage: {
    width: 104,
    height: 104,
    position: "absolute",
    alignSelf: "center",
  },
  companyIconText: {
    fontSize: 40,
    fontWeight: "600",
    color: "#6B7280",
  },
  title: {
    fontSize: 30,
    fontWeight: "500",
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 12,
    lineHeight: 36,
    letterSpacing: -0.3,
  },
  subtitle: {
    fontSize: 15,
    color: "#FFFFFF",
    textAlign: "center",
    lineHeight: 23,
    letterSpacing: -0.3,
    marginBottom: 26,
    marginHorizontal: 64,
  },
  inviteText: {
    fontSize: 15,
    color: "#FFFFFF",
    textAlign: "center",
    lineHeight: 23,
    letterSpacing: -0.3,
  },
});

export default OfferSuccessModal;
